import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles, getAllCategories } from '../data/articles/index';
import { Article } from '../data/articleTypes';

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-IE', options);
};

const Articles: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const categories = getAllCategories();
  
  const filteredArticles = selectedCategory 
    ? articles.filter((article) => article.category === selectedCategory)
    : articles;

  return (
    <div className="pt-48 pb-40 md:pt-48 md:pb-48">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">
            <span className="text-white">Our</span>
            <span className="block mt-4 bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">Articles</span>
          </h1>
          <p className="text-[#FFFFFFCC] mt-6 max-w-3xl mx-auto text-lg">
            Insights, guidance, and resources for preserving the legacy of your loved ones.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              selectedCategory === null
                ? 'bg-primary text-background'
                : 'bg-[#1A1A1A] text-[#FFFFFFCC] hover:bg-[#222222] hover:text-white'
            }`}
          >
            All
          </button>
          
          {categories.map((category: string) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                selectedCategory === category
                  ? 'bg-primary text-background'
                  : 'bg-[#1A1A1A] text-[#FFFFFFCC] hover:bg-[#222222] hover:text-white'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-6 lg:gap-8">
          {filteredArticles.map((article: Article) => (
            <Link 
              to={`/articles/${article.slug}`} 
              key={article.id}
              className="group"
            >
              <article className="bg-[#1A1A1A] rounded-xl overflow-hidden transition-transform duration-300 h-full flex flex-col hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1">
                {/* Image Container */}
                <div className="relative h-52 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent opacity-70 z-10"/>
                  <img 
                    src={article.coverImage} 
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.src = '/images/article-placeholder.jpg';
                    }}
                  />
                </div>
                
                {/* Content */}
                <div className="flex flex-col flex-grow p-6">
                  {/* Category & Date */}
                  <div className="flex justify-between items-center mb-3 text-xs text-[#FFFFFFAA]">
                    <span className="bg-[#333333] px-3 py-1 rounded-full">{article.category}</span>
                    <span>{formatDate(article.date)}</span>
                  </div>
                  
                  {/* Title */}
                  <h2 className="text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h2>
                  
                  {/* Excerpt */}
                  <p className="text-[#FFFFFFCC] text-sm mb-4 flex-grow line-clamp-3">
                    {article.excerpt}
                  </p>
                  
                  {/* Read Time */}
                  <div className="flex justify-end items-center mt-auto pt-4 border-t border-[#333333] text-xs text-[#FFFFFFAA]">
                    <span>{article.readTime} min read</span>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>
        
        {/* Empty State */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <h3 className="text-2xl font-semibold text-white mb-4">No articles found</h3>
            <p className="text-[#FFFFFFCC]">
              No articles match the selected category. Try selecting a different category.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles; 